import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  addDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import ReactMarkdown from "react-markdown";
import grayBackground from "../images/gray-background.png";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getTextAlignment } from "../utils/helpers";

function LessonSteps({ userType = "user" }) {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lessonTasks, setLessonTasks] = useState([]);
  const [lesson, setLesson] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(true);

  // Check if lesson is already completed
  const checkLessonCompletion = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setCheckingCompletion(false);
        return;
      }

      const activityRef = collection(db, "user_activity");
      const q = query(
        activityRef,
        where("user_id", "==", doc(db, "users", currentUser.uid)),
        where("type", "==", "completed_lesson"),
        where("lesson_id", "==", lessonId)
      );
      const querySnapshot = await getDocs(q);

      const isCompleted = !querySnapshot.empty;
      setLessonCompleted(isCompleted);
      setCheckingCompletion(false);
    } catch (error) {
      console.error("Error checking lesson completion:", error);
      setCheckingCompletion(false);
    }
  };

  // Track lesson completion
  const trackLessonCompletion = async () => {
    // Only track completion once
    if (lessonCompleted) {
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found");
        return;
      }

      // Get user data to find organization_id
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (!userDoc.exists()) {
        console.error("User document not found");
        return;
      }

      const userData = userDoc.data();

      // Create lesson completion activity record
      const activityData = {
        created_date: new Date(),
        user_id: doc(db, "users", currentUser.uid),
        organization_id: userData.organization_id || null, // Handle cases where org_id might be null/undefined
        type: "completed_lesson",
        lesson_id: lessonId,
      };

      // Add to user_activity collection
      const activityRef = collection(db, "user_activity");
      await addDoc(activityRef, activityData);

      console.log("Lesson completion tracked successfully:", activityData);
      setLessonCompleted(true);
      toast.success("Lesson marked complete 🎉");
    } catch (error) {
      console.error("Error tracking lesson completion:", error);
      // Still show success message to user, but log the error for debugging
      toast.success("Lesson marked complete 🎉");
    }
  };

  // Fetch lesson and check if it's in preview mode
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const lessonRef = doc(db, "node_lessons", lessonId);
        const lessonSnap = await getDoc(lessonRef);

        if (lessonSnap.exists()) {
          const lessonData = {
            id: lessonSnap.id,
            ...lessonSnap.data(),
          };

          setLesson(lessonData);
        } else {
          setError("Lesson not found");
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching lesson:", error);
        setError("Failed to load lesson");
        setLoading(false);
      }
    };

    if (lessonId) {
      fetchLesson();
    }
  }, [lessonId, userType]);

  // Fetch lesson tasks from Firestore
  useEffect(() => {
    const fetchLessonTasks = async () => {
      try {
        const lessonTasksRef = collection(db, "node_lesson_tasks");
        const lessonRef = doc(db, "node_lessons", lessonId);
        const q = query(
          lessonTasksRef,
          where("lesson_id", "==", lessonRef),
          orderBy("order", "asc")
        );
        const querySnapshot = await getDocs(q);

        const tasksData = [];
        querySnapshot.forEach((doc) => {
          tasksData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setLessonTasks(tasksData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lesson tasks:", error);
        setError("Failed to load lesson tasks");
        setLoading(false);
      }
    };

    if (lessonId && lesson) {
      fetchLessonTasks();
    }
  }, [lessonId, lesson]);

  // Check lesson completion status when lesson loads
  useEffect(() => {
    if (lesson && currentStep === lessonTasks.length) {
      checkLessonCompletion();
    }
  }, [lesson, currentStep, lessonTasks.length]);

  const handleNext = () => {
    if (currentStep < lessonTasks.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    navigate(`/lesson/${lessonId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-600">Loading lesson steps...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (lessonTasks.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">
              <p className="text-gray-600">No lesson steps found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLastStep = currentStep === lessonTasks.length;
  const currentTask = isLastStep ? null : lessonTasks[currentStep];

  return (
    <>
      <style jsx>{`
        @keyframes bounceIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      <div className="min-h-screen bg-white">
        {/* Lesson Title Header - always show */}
        <div className="flex justify-between items-center p-6 pb-0">
          <div className="w-16"></div>
          <h1 className="text-lg font-normal text-gray-600 text-center flex-1 max-w-4xl">
            {lesson?.title || "Lesson"}
          </h1>
          <div className="w-16 flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="bg-primary-500 text-white p-2 rounded-lg hover:bg-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              aria-label="Close lesson"
            >
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="px-3 sm:px-6 pb-24">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg p-4 sm:p-8 pt-4 min-h-[450px] bg-white">
              {isLastStep ? (
                <div className="space-y-6">
                  <div
                    className="rounded-lg px-6 sm:px-14 py-12 pt-8 min-h-[450px] flex items-center justify-center"
                    style={{
                      backgroundImage: `url(${grayBackground})`,
                      backgroundRepeat: "repeat",
                      backgroundSize: "auto",
                    }}
                  >
                    <div className="text-center py-12 pt-6">
                      {checkingCompletion ? (
                        // Loading state while checking completion
                        <div>
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
                          <p className="text-gray-600">
                            Checking completion status...
                          </p>
                        </div>
                      ) : lessonCompleted ? (
                        // Already completed - show artifact and gallery button
                        <>
                          <h2 className="text-3xl font-bold text-gray-900 mb-6">
                            Lesson Completed
                          </h2>
                          <p className="text-xl text-gray-700 mb-6">
                            You've discovered a new artifact!
                          </p>

                          {/* Artifact Display */}
                          {lesson?.artifact_image && (
                            <div className="flex justify-center mb-8">
                              <img
                                src={lesson.artifact_image}
                                alt="Discovered artifact"
                                className="w-48 h-48 object-contain"
                              />
                            </div>
                          )}

                          <button
                            onClick={() => navigate("/gallery")}
                            className="bg-primary-500 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg"
                          >
                            Go to Explorer's Gallery
                          </button>
                        </>
                      ) : (
                        // Not completed yet - show celebration and completion button
                        <>
                          <h2 className="text-3xl font-bold text-gray-900 mb-6">
                            Congratulations!
                          </h2>
                          <p className="text-xl text-gray-700 mb-6">
                            You've completed the lesson. Great job!
                          </p>
                          <div className="text-8xl mb-8">🎉</div>
                          <button
                            onClick={async () => {
                              await trackLessonCompletion();
                            }}
                            className="bg-primary-500 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg"
                          >
                            Mark Lesson Complete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Content based on media_type */}
                  {currentTask.media_type === "image" && (
                    <div
                      className="rounded-lg px-6 sm:px-14 py-12 pt-8 min-h-[350px]"
                      style={{
                        backgroundImage: `url(${grayBackground})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "auto",
                      }}
                    >
                      {/* Task Title */}
                      <h3 className="text-xl font-bold text-gray-900 text-center mb-6">
                        {currentTask.title}
                      </h3>

                      <div className="flex flex-col gap-3">
                        {/* Text Content */}
                        {currentTask.image_text && (
                          <div
                            className={`text-gray-700 leading-relaxed prose prose-gray max-w-none text-lg ${getTextAlignment(
                              currentTask.text_alignment,
                              currentTask.image_text
                            )}`}
                          >
                            <ReactMarkdown
                              components={{
                                ol: ({ children }) => (
                                  <ol className="list-decimal list-inside space-y-1 mb-4">
                                    {children}
                                  </ol>
                                ),
                                ul: ({ children }) => (
                                  <ul className="list-disc list-inside space-y-1 mb-4">
                                    {children}
                                  </ul>
                                ),
                                li: ({ children }) => (
                                  <li className="ml-4">{children}</li>
                                ),
                                strong: ({ children }) => (
                                  <strong className="font-bold text-gray-900">
                                    {children}
                                  </strong>
                                ),
                                p: ({ children }) => (
                                  <p className="mb-4">{children}</p>
                                ),
                              }}
                            >
                              {currentTask.image_text}
                            </ReactMarkdown>
                          </div>
                        )}

                        {/* Image Content */}
                        {currentTask.image_path && (
                          <div className="flex justify-center">
                            <img
                              src={currentTask.image_path}
                              alt="Lesson content"
                              className="w-full max-w-[90vw] max-h-[400px] h-auto rounded-lg object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {currentTask.media_type === "text" && (
                    <div
                      className="rounded-lg px-6 sm:px-12 py-12 pt-8 min-h-[350px]"
                      style={{
                        backgroundImage: `url(${grayBackground})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "auto",
                      }}
                    >
                      {/* Task Title */}
                      <h3 className="text-xl font-bold text-gray-900 text-center mb-6">
                        {currentTask.title}
                      </h3>

                      {/* For text type, only show text_detail if it exists and is different from image_text */}
                      {currentTask.text_detail && (
                        <div
                          className={`text-gray-700 leading-relaxed prose prose-gray max-w-none text-lg ${getTextAlignment(
                            currentTask.text_alignment,
                            currentTask.text_detail
                          )}`}
                        >
                          <ReactMarkdown
                            components={{
                              // Override ol and li to render as regular paragraphs for lesson steps
                              ol: ({ children }) => (
                                <ol className="list-decimal list-outside space-y-2">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="mb-4">{children}</li>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc list-inside space-y-1 mb-4">
                                  {children}
                                </ul>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-bold text-gray-900">
                                  {children}
                                </strong>
                              ),
                              p: ({ children }) => (
                                <p className="mb-2">{children}</p>
                              ),
                            }}
                          >
                            {currentTask.text_detail}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}

                  {currentTask.media_type === "video" && (
                    <div className="flex justify-center">
                      <div className="w-full max-w-[90vw]">
                        <iframe
                          src={currentTask.video_url}
                          width="100%"
                          height="400"
                          className="w-full h-[300px] md:h-[400px] lg:h-[500px] xl:h-[600px] rounded-lg"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Lesson video"
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-center items-center space-x-4 max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`p-3 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                currentStep === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-primary-500 text-white hover:bg-primary-700"
              }`}
              aria-label="Previous step"
            >
              <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
            </button>

            <span className="text-gray-700 font-medium">
              Step {currentStep + 1} of {lessonTasks.length + 1}
            </span>

            <button
              type="button"
              onClick={handleNext}
              disabled={currentStep === lessonTasks.length}
              className={`p-3 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                currentStep === lessonTasks.length
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-primary-500 text-white hover:bg-primary-700"
              }`}
              aria-label="Next step"
            >
              <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LessonSteps;
